import { useState, useEffect } from "react";
import { X, Smartphone, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface InstallPromptBannerProps {
  variant?: "public" | "admin";
  position?: "top" | "bottom";
}

const STORAGE_KEY = "pwa-prompt-dismissed";
const COOLDOWN_DAYS = 7;

const InstallPromptBanner = ({ 
  variant = "public", 
  position = "top" 
}: InstallPromptBannerProps) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia("(display-mode: standalone)").matches 
      || (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    // Check dismissal cooldown
    const lastDismissed = localStorage.getItem(STORAGE_KEY);
    if (lastDismissed) {
      const dismissedTime = parseInt(lastDismissed, 10);
      const cooldownMs = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < cooldownMs) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show iOS-specific banner
      setIsVisible(true);
      return;
    }

    // Listen for beforeinstallprompt event (Chrome/Edge/Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setIsVisible(false);
  };

  if (!isVisible || isStandalone) return null;

  const isPublic = variant === "public";

  return (
    <div
      className={cn(
        "relative flex items-center justify-between gap-3 px-4 py-3 animate-in slide-in-from-top-2 duration-300",
        position === "bottom" && "slide-in-from-bottom-2",
        isPublic
          ? "bg-gradient-to-r from-secondary to-secondary/90 text-white"
          : "bg-muted/80 border-b border-border text-foreground"
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Smartphone className="h-5 w-5 shrink-0" />
        <p className="text-sm font-medium truncate">
          {isIOS ? (
            <>
              Add to Home Screen: Tap <Share className="inline h-4 w-4 mx-0.5" /> then <Plus className="inline h-4 w-4 mx-0.5" /> Add to Home
            </>
          ) : isPublic ? (
            "Get the Roots Football app for quick access!"
          ) : (
            "Install app for faster access to admin"
          )}
        </p>
      </div>
      
      <div className="flex items-center gap-2 shrink-0">
        {isIOS ? (
          <Button
            asChild
            size="sm"
            variant={isPublic ? "secondary" : "outline"}
            className={cn(
              "h-8 text-xs",
              isPublic && "bg-white/20 hover:bg-white/30 text-white border-white/30"
            )}
          >
            <Link to="/install">More Info</Link>
          </Button>
        ) : (
          <Button
            onClick={handleInstall}
            size="sm"
            variant={isPublic ? "secondary" : "default"}
            className={cn(
              "h-8 text-xs",
              isPublic && "bg-white/20 hover:bg-white/30 text-white border-white/30"
            )}
          >
            {isPublic ? "Install" : "Get App"}
          </Button>
        )}
        <button
          onClick={handleDismiss}
          className={cn(
            "p-1.5 rounded-md transition-colors",
            isPublic
              ? "hover:bg-white/20 text-white/80 hover:text-white"
              : "hover:bg-accent text-muted-foreground hover:text-foreground"
          )}
          aria-label="Dismiss install prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPromptBanner;
